import { useState, useRef, useEffect, useCallback } from 'react';
import { FormInstance } from 'antd';
import { message } from 'antd';
import { useIntl } from 'react-intl';
import { options } from '../config/config';
import { DraftSaveStatus } from '../types/ListingData';

interface UseAutoSaveOptions {
  form: FormInstance;
  isOpen: boolean;
  isSaving: boolean;
  isUploading?: boolean;
  onSave: (context?: string) => void;
  getLastSavedData: () => Record<string, unknown> | null;
  setLastSavedData: (data: Record<string, unknown>) => void;
  getAdditionalData?: () => Record<string, unknown>;
  isEnabled?: () => boolean;
  onValidationError?: () => boolean;
  normalizeData?: (data: Record<string, unknown>) => Record<string, unknown>;
  compareFields?: string[];
}

interface UseAutoSaveReturn {
  draftSaveStatus: DraftSaveStatus;
  hasUnsavedChanges: boolean;
  checkForUnsavedChanges: () => boolean;
  saveDraft: () => Promise<void>;
  saveDraftPromise: (context?: string) => Promise<void>;
  resetAutoSave: () => void;
  setDraftSaveStatus: (status: DraftSaveStatus) => void;
}

export const useAutoSave = ({
  form,
  isOpen,
  isSaving,
  isUploading = false,
  onSave,
  getLastSavedData,
  setLastSavedData,
  getAdditionalData,
  isEnabled,
  onValidationError,
  normalizeData,
  compareFields,
}: UseAutoSaveOptions): UseAutoSaveReturn => {
  const intl = useIntl();
  const [draftSaveStatus, setDraftSaveStatus] = useState<DraftSaveStatus>({
    status: 'idle',
  });
  const [hasUnsavedChanges, setHasUnsavedChanges] = useState<boolean>(false);
  const autoSaveInterval = useRef<NodeJS.Timeout | null>(null);
  const errorTimeoutRef = useRef<NodeJS.Timeout | null>(null);

  const checkForUnsavedChanges = useCallback(() => {
    if (!isOpen) {
      return false;
    }

    let currentData;
    try {
      currentData = form.getFieldsValue(true);
    } catch (error) {
      return false;
    }

    const additionalData = getAdditionalData ? getAdditionalData() : {};
    const lastSavedData = getLastSavedData();

    const fullCurrentData = {
      ...currentData,
      ...additionalData,
    };

    if (compareFields && compareFields.length > 0) {
      compareFields.forEach((field) => {
        if (!(field in fullCurrentData)) {
          fullCurrentData[field] = undefined;
        }
      });
    }

    const normalizedCurrentData = normalizeData
      ? normalizeData(fullCurrentData)
      : fullCurrentData;
    const normalizedLastSavedData =
      normalizeData && lastSavedData
        ? normalizeData(lastSavedData)
        : lastSavedData;

    let finalCurrentData = normalizedCurrentData;
    let finalLastSavedData = normalizedLastSavedData;

    if (compareFields && compareFields.length > 0) {
      finalCurrentData = compareFields.reduce(
        (acc, field) => {
          if (normalizedCurrentData && field in normalizedCurrentData) {
            acc[field] = normalizedCurrentData[field];
          }
          return acc;
        },
        {} as Record<string, unknown>
      );

      finalLastSavedData = compareFields.reduce(
        (acc, field) => {
          if (normalizedLastSavedData && field in normalizedLastSavedData) {
            acc[field] = normalizedLastSavedData[field];
          }
          return acc;
        },
        {} as Record<string, unknown>
      );
    }

    const hasChanges =
      JSON.stringify(finalCurrentData) !== JSON.stringify(finalLastSavedData);
    setHasUnsavedChanges(hasChanges);

    if (hasChanges && draftSaveStatus.status === 'error') {
      setDraftSaveStatus({ status: 'idle' });
    }

    return hasChanges;
  }, [
    form,
    getLastSavedData,
    getAdditionalData,
    draftSaveStatus.status,
    normalizeData,
    compareFields,
    isOpen,
  ]);

  const stopAutoSave = useCallback(() => {
    if (autoSaveInterval.current) {
      clearInterval(autoSaveInterval.current);
      autoSaveInterval.current = null;
    }

    if (errorTimeoutRef.current) {
      clearTimeout(errorTimeoutRef.current);
      errorTimeoutRef.current = null;
    }
  }, []);

  const saveDraft = useCallback(async () => {
    if (isSaving || isUploading) {
      return;
    }

    if (onValidationError && onValidationError()) {
      message.error(intl.formatMessage({ id: 'validation.fix_errors' }));
      return;
    }

    const hasChanges = checkForUnsavedChanges();
    if (!hasChanges) {
      return;
    }

    setDraftSaveStatus({ status: 'saving' });
    message.loading(intl.formatMessage({ id: 'general.saving_draft' }), 0);

    onSave();
  }, [
    isSaving,
    isUploading,
    onValidationError,
    checkForUnsavedChanges,
    intl,
    onSave,
  ]);

  const saveDraftPromise = useCallback(
    async (context?: string) => {
      if (isSaving || isUploading) {
        return;
      }

      if (onValidationError && onValidationError()) {
        const errorMessage = intl.formatMessage({
          id: 'validation.fix_errors',
        });
        message.error(errorMessage);
      }

      const hasChanges = checkForUnsavedChanges();
      if (!hasChanges) {
        return;
      }

      return new Promise<void>((resolve, reject) => {
        window.saveDraftResolve = resolve;
        window.saveDraftReject = reject;

        setDraftSaveStatus({ status: 'saving' });
        message.loading(intl.formatMessage({ id: 'general.saving_draft' }), 0);

        onSave(context);
      });
    },
    [
      isSaving,
      isUploading,
      onValidationError,
      checkForUnsavedChanges,
      intl,
      onSave,
    ]
  );

  const startAutoSave = useCallback(() => {
    if (!autoSaveInterval.current) {
      autoSaveInterval.current = setInterval(() => {
        if (!isOpen) {
          return;
        }

        const enabled = isEnabled ? isEnabled() : true;

        if (
          hasUnsavedChanges &&
          !isSaving &&
          !isUploading &&
          draftSaveStatus.status !== 'saving' &&
          draftSaveStatus.status !== 'error' &&
          enabled
        ) {
          saveDraft();
        }
      }, options.autoSave.interval);
    }
  }, [
    hasUnsavedChanges,
    isSaving,
    isUploading,
    draftSaveStatus.status,
    saveDraft,
    isOpen,
    isEnabled,
  ]);

  const resetAutoSave = useCallback(() => {
    setHasUnsavedChanges(false);
    setDraftSaveStatus({ status: 'idle' });
    stopAutoSave();
  }, [stopAutoSave]);

  const handleSetDraftSaveStatus = useCallback((status: DraftSaveStatus) => {
    setDraftSaveStatus(status);

    if (status.status === 'error') {
      if (errorTimeoutRef.current) {
        clearTimeout(errorTimeoutRef.current);
      }

      errorTimeoutRef.current = setTimeout(() => {
        setDraftSaveStatus({ status: 'idle' });
      }, options.autoSave.errorTimeout);
    }
  }, []);

  useEffect(() => {
    if (!isOpen) {
      stopAutoSave();
    }
  }, [isOpen, stopAutoSave]);

  useEffect(() => {
    startAutoSave();
    return () => {
      stopAutoSave();
    };
  }, [startAutoSave, stopAutoSave]);

  useEffect(() => {
    if (!isOpen) {
      return;
    }
    const hasChanges = checkForUnsavedChanges();
    setHasUnsavedChanges(hasChanges);
  }, [checkForUnsavedChanges, isOpen]);

  return {
    draftSaveStatus,
    hasUnsavedChanges,
    checkForUnsavedChanges,
    saveDraft,
    saveDraftPromise,
    resetAutoSave,
    setDraftSaveStatus: handleSetDraftSaveStatus,
  };
};
